import PageHeader from "@/components/pageHeader";
import { ArrowRight } from "lucide-react";
import { useEffect, useRef,useState } from "react";
import { Link } from "react-router-dom";

export const JourneyPage = () => {
  const milestones = [
    { year: "2001", title: "The Beginning", desc: "The vision took shape with the establishment of Gillco Developers, marking the beginning of a legacy built on trust and excellence." },
    { year: "2004", title: "Gillco Valley", desc: "Gillco made history by developing one of the largest townships in Kharar-Mohali — the iconic Gillco Valley." },
    { year: "2005", title: "Residential Growth", desc: "Launch of Gillco Valley Extension 1, Gillco Complex, and Gillco Villas, strengthening Gillco\'s residential footprint.", link: `/projects/villas` },
    { year: "2006", title: "Urban Living", desc: "Launch of Gillco Towers, introducing elevated urban living experiences.", link: `/projects/towers` },
    { year: "2008", title: "Expanding Horizons", desc: "Launch of Gillco Heights and Gillco Independent Homes, expanding the Group\'s diverse residential offerings.", link: `/projects/heights` },
    { year: "2012", title: "Accessible Homes", desc: "Launch of Gillco Budget Homes, bringing quality housing within reach of aspiring homeowners.", link: `/projects/floors` },
    { year: "2013", title: "Premium & Education", desc: "Launch of Gillco Palms, Bungalows, Heights Extension. Establishment of Gillco International School.", link: `/projects/palms` }, 
    { year: "2015", title: "Parkhills", desc: "Launch of Gillco Parkhills, further redefining community living.", link: `/projects/parkhills` },
    { year: "2018", title: "Industrial Park", desc: "Entry into industrial infrastructure with the launch of Gillco Industrial Park, a 200-acre development.", link: `/projects/industrial-park` },
    { year: "2021", title: "Gillco Palms 2BHK", desc: "Launch of thoughtfully designed 2 BHK residences at Gillco Palms.", link: `/projects/palms` },
    { year: "2022", title: "Commercial Debut", desc: "Launch of Gillco Business Centre across 6.24 acres in Sector 126, pioneering the Group\'s commercial portfolio." },
    { year: "2024", title: "Studio Apartments", desc: "Launch of Gillco Studio Apartments, catering to evolving urban lifestyles and modern preferences." },
    { year: "2025", title: "Business Centre II", desc: "Launch of Gillco Business Centre in Sector 115, strengthening Gillco\'s presence in the commercial segment." },
  ];

  return (
    <div data-testid="v13-journey-page">
      <PageHeader title="Our Journey" subtitle="Milestones" backgroundImage="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920" />

      {/* Intro Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="flex gap-8 lg:gap-16">
            <div className="hidden md:flex flex-col items-center">
              <span className="text-[11px] tracking-[0.3em] uppercase text-[#C4A265]" style={{ writingMode: "vertical-lr", letterSpacing: "0.3em" }}>MILESTONES</span>
              <div className="w-[1px] bg-[#C4A265] flex-1 mt-4" />
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl text-[#0A0A0A] mb-8 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>A Journey of<br />Purpose & Progress</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-6" style={{ textAlign: "justify" }}>Great legacies are not built overnight. The foundations of Gillco were laid in the early years of Mr Ranjit Singh Gill's entrepreneurial journey as a brick kiln owner, where resilience, foresight and an unwavering work ethic shaped his vision for the future.</p>
              <p className="text-[#4A4A4A] leading-relaxed" style={{ textAlign: "justify" }}>Between 1996 and 1998, this vision evolved into a larger ambition – creating thoughtfully planned developments that would redefine urban living in Punjab. The year 2001 marked a defining chapter in this journey. From that point onward, Gillco became a symbol of progress and enduring community development in Punjab.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Vertical Scroll Timeline */}
      <VerticalTimeline milestones={milestones} />

      {/* Closing Quote */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <div className="text-5xl text-[#C4A265] leading-none" style={{ fontFamily: "'Cormorant Garamond', serif" }}>&ldquo;</div>
          <p className="text-xl lg:text-2xl text-[#C4A265] italic leading-relaxed my-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>A legacy-driven journey marked by consistent growth, visionary leadership, and enduring trust across generations</p>
          <div className="text-5xl text-[#C4A265] leading-none" style={{ fontFamily: "'Cormorant Garamond', serif" }}>&rdquo;</div>
        </div>
      </section>
    </div>
  );
};

const VerticalTimeline = ({ milestones }) => {
  const trackRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      if (!trackRef.current) return;
      const rect = trackRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = rect.height;
      const scrolled = vh * 0.4 - rect.top;
      setProgress(Math.max(0, Math.min(1, scrolled / total)));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section className="py-20 lg:py-32 bg-[#f8f6f2]">
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        <div ref={trackRef} className="relative">
          {/* Center line - background track */}
          <div className="absolute left-6 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-[1px] bg-[#C4A265]/15" />
          {/* Center line - animated fill */}
          <div
            className="absolute left-6 md:left-1/2 md:-translate-x-1/2 top-0 w-[2px] bg-[#C4A265] origin-top"
            style={{ height: `${progress * 100}%`, transition: "height 0.1s linear" }}
          />

          <div className="space-y-0">
            {milestones.map((m, i) => (
              <TimelineNode key={i} milestone={m} index={i} isLeft={i % 2 === 0} progress={progress} total={milestones.length} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const TimelineNode = ({ milestone, index, isLeft, progress, total }) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.unobserve(el); } }, { threshold: 0.3 });
    obs.observe(el); return () => obs.disconnect();
  }, []);

  const threshold = index / total;
  const dotActive = progress >= threshold;

  return (
    <div ref={ref} className={`relative flex items-start md:items-center ${isLeft ? "md:flex-row-reverse" : ""}`}>
      {/* Dot on the line */}
      <div className="absolute left-6 md:left-1/2 -translate-x-1/2 z-10">
        <div
          className="w-4 h-4 rounded-full border-2 transition-all duration-700"
          style={{
            borderColor: dotActive ? "#C4A265" : "rgba(196,162,101,0.2)",
            backgroundColor: dotActive ? "#C4A265" : "transparent",
            transform: dotActive ? "scale(1)" : "scale(0.6)",
            boxShadow: dotActive ? "0 0 0 6px rgba(196,162,101,0.12)" : "none",
          }}
        />
      </div>

      {/* Content card */}
      <div
        className={`ml-16 md:ml-0 md:w-[44%] ${isLeft ? "md:mr-auto md:pr-16" : "md:ml-auto md:pl-16"} py-10`}
        style={{
          opacity: vis ? 1 : 0,
          transform: vis ? "translateX(0) translateY(0)" : `translateX(${isLeft ? "40px" : "-40px"}) translateY(20px)`,
          transition: `all 0.9s cubic-bezier(0.16,1,0.3,1) 0.15s`,
        }}
      >
        {/* Year - large watermark */}
        <div className="relative mb-1">
          <span
            className="text-7xl lg:text-8xl font-light text-[#0A4F3B]/15 leading-none select-none"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {milestone.year}
          </span>
        </div>
        {/* Gold accent line */}
        <div
          className="h-[2px] w-12 bg-[#C4A265] mb-4"
          style={{
            transform: vis ? "scaleX(1)" : "scaleX(0)",
            transformOrigin: isLeft ? "right" : "left",
            transition: `transform 0.8s cubic-bezier(0.16,1,0.3,1) 0.4s`,
          }}
        />
        {/* Title */}
        <h3 className="text-lg lg:text-xl text-[#0A0A0A] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{milestone.title}</h3>
        {/* Description */}
        <p className="text-sm text-[#4A4A4A] leading-relaxed">{milestone.desc}</p>
        {/* Optional project link */}
        {milestone.link && (
          <Link
            to={milestone.link}
            className="inline-flex items-center gap-2 mt-4 text-[11px] uppercase tracking-[0.25em] text-[#0A4F3B] hover:gap-3 transition-all duration-300 group"
            data-testid={`v13-journey-link-${milestone.year}`}
          >
            <span className="border-b border-[#0A4F3B]/40 group-hover:border-[#0A4F3B] pb-0.5">View Project</span>
            <ArrowRight size={12} className="text-[#C4A265]" />
          </Link>
        )}
      </div>
    </div>
  );
};